/**
 * Leviathan Web - Contract Form Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  category: string;
  costPerUnit: number | null;
  pricePerUnit: number | null;
  unit: string | null;
}

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
}

interface ContractPrice {
  productId: string;
  product?: Product;
  fixedPrice: number | null;
  rackPlusAmount: number | null;
  marginPercent: number | null;
  costPlusAmount: number | null;
  minPrice: number | null;
  maxPrice: number | null;
}

interface ContractFormData {
  customerId: string;
  contractNumber: string;
  status: string;
  startDate: string;
  endDate: string;
  volumeCommitment: string;
  notes: string;
  prices: ContractPrice[];
}

const PRICING_METHODS = [
  { value: 'FIXED', label: 'Fixed Price', field: 'fixedPrice' },
  { value: 'RACK_PLUS', label: 'Rack + Amount', field: 'rackPlusAmount' },
  { value: 'MARGIN_PERCENT', label: 'Margin %', field: 'marginPercent' },
  { value: 'COST_PLUS', label: 'Cost + Amount', field: 'costPlusAmount' },
];

const ContractFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = !!id;

  const [formData, setFormData] = useState<ContractFormData>({
    customerId: '',
    contractNumber: '',
    status: 'DRAFT',
    startDate: new Date().toISOString().split('T')[0] || '',
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] || '',
    volumeCommitment: '',
    notes: '',
    prices: [],
  });

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pricingMethod, setPricingMethod] = useState('FIXED');

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    if (isEdit) {
      fetchContract();
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers?limit=1000`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?limit=1000`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchContract = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const contract = data.contract;
        setFormData({
          customerId: contract.customer.id,
          contractNumber: contract.contractNumber,
          status: contract.status,
          startDate: contract.startDate.split('T')[0],
          endDate: contract.endDate.split('T')[0],
          volumeCommitment: contract.volumeCommitment?.toString() || '',
          notes: contract.notes || '',
          prices: contract.prices.map((p: any) => ({
            productId: p.product.id,
            product: p.product,
            fixedPrice: p.fixedPrice,
            rackPlusAmount: p.rackPlusAmount,
            marginPercent: p.marginPercent,
            costPlusAmount: p.costPlusAmount,
            minPrice: p.minPrice,
            maxPrice: p.maxPrice,
          })),
        });
        // Determine pricing method from first price entry
        if (contract.prices.length > 0) {
          const firstPrice = contract.prices[0];
          if (firstPrice.fixedPrice !== null) setPricingMethod('FIXED');
          else if (firstPrice.rackPlusAmount !== null) setPricingMethod('RACK_PLUS');
          else if (firstPrice.marginPercent !== null) setPricingMethod('MARGIN_PERCENT');
          else if (firstPrice.costPlusAmount !== null) setPricingMethod('COST_PLUS');
        }
      } else {
        navigate('/pricing/contracts');
      }
    } catch (error) {
      console.error('Failed to fetch contract:', error);
      navigate('/pricing/contracts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleAddProduct = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product || formData.prices.some((p) => p.productId === productId)) return;

    setFormData((prev) => ({
      ...prev,
      prices: [
        ...prev.prices,
        {
          productId,
          product,
          fixedPrice: null,
          rackPlusAmount: null,
          marginPercent: null,
          costPlusAmount: null,
          minPrice: null,
          maxPrice: null,
        },
      ],
    }));
  };

  const handleAddAllProducts = () => {
    const existingIds = formData.prices.map((p) => p.productId);
    const newPrices = products
      .filter((p) => !existingIds.includes(p.id))
      .map((product) => ({
        productId: product.id,
        product,
        fixedPrice: null,
        rackPlusAmount: null,
        marginPercent: null,
        costPlusAmount: null,
        minPrice: null,
        maxPrice: null,
      }));

    setFormData((prev) => ({
      ...prev,
      prices: [...prev.prices, ...newPrices],
    }));
  };

  const handleRemoveProduct = (productId: string) => {
    setFormData((prev) => ({
      ...prev,
      prices: prev.prices.filter((p) => p.productId !== productId),
    }));
  };

  const handlePriceChange = (productId: string, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      prices: prev.prices.map((p) =>
        p.productId === productId
          ? { ...p, [field]: value === '' ? null : parseFloat(value) }
          : p
      ),
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.contractNumber.trim()) {
      newErrors.contractNumber = 'Contract number is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = {
        customerId: formData.customerId,
        contractNumber: formData.contractNumber,
        status: formData.status,
        startDate: formData.startDate,
        endDate: formData.endDate,
        volumeCommitment: formData.volumeCommitment ? parseInt(formData.volumeCommitment) : null,
        notes: formData.notes || null,
        prices: formData.prices.map((p) => ({
          productId: p.productId,
          fixedPrice: p.fixedPrice,
          rackPlusAmount: p.rackPlusAmount,
          marginPercent: p.marginPercent,
          costPlusAmount: p.costPlusAmount,
          minPrice: p.minPrice,
          maxPrice: p.maxPrice,
        })),
      };

      const url = isEdit ? `${API_URL}/api/contracts/${id}` : `${API_URL}/api/contracts`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/pricing/contracts/${data.contract.id}`);
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to save contract');
      }
    } catch (error) {
      console.error('Failed to save contract:', error);
      alert('Failed to save contract');
    } finally {
      setIsSaving(false);
    }
  };

  const getPriceField = (): 'fixedPrice' | 'rackPlusAmount' | 'marginPercent' | 'costPlusAmount' => {
    const field = PRICING_METHODS.find((m) => m.value === pricingMethod)?.field;
    return (field as 'fixedPrice' | 'rackPlusAmount' | 'marginPercent' | 'costPlusAmount') || 'fixedPrice';
  };

  const getPriceLabel = () => {
    return PRICING_METHODS.find((m) => m.value === pricingMethod)?.label || 'Price';
  };

  const getPriceValue = (entry: ContractPrice): number | null => {
    const field = getPriceField();
    return entry[field];
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return `$${price.toFixed(4)}`;
  };

  const availableProducts = products.filter(
    (p) => !formData.prices.some((fp) => fp.productId === p.id)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading contract...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <Link
            to="/pricing/contracts"
            className="text-text-tertiary hover:text-amber font-mono text-sm mb-2 inline-block"
          >
            ← Back to Contracts
          </Link>
          <h1
            className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
            style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
          >
            {isEdit ? 'Edit Contract' : 'New Contract'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <GeometricPanel>
          <h2 className="text-lg font-display text-steel uppercase tracking-wider mb-4">
            Contract Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Customer *
              </label>
              <select
                name="customerId"
                value={formData.customerId}
                onChange={handleInputChange}
                className={`w-full bg-background border ${
                  errors.customerId ? 'border-error-red' : 'border-panel-border'
                } focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm`}
              >
                <option value="">Select customer...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} (#{customer.customerNumber})
                  </option>
                ))}
              </select>
              {errors.customerId && (
                <p className="text-error-red text-xs font-mono mt-1">{errors.customerId}</p>
              )}
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Contract Number *
              </label>
              <input
                type="text"
                name="contractNumber"
                value={formData.contractNumber}
                onChange={handleInputChange}
                placeholder="e.g., CTR-2024-001"
                className={`w-full bg-background border ${
                  errors.contractNumber ? 'border-error-red' : 'border-panel-border'
                } focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm`}
              />
              {errors.contractNumber && (
                <p className="text-error-red text-xs font-mono mt-1">{errors.contractNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                <option value="DRAFT">Draft</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING_RENEWAL">Pending Renewal</option>
                <option value="EXPIRED">Expired</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Volume Commitment (gallons)
              </label>
              <input
                type="number"
                name="volumeCommitment"
                value={formData.volumeCommitment}
                onChange={handleInputChange}
                placeholder="Optional"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full bg-background border ${
                  errors.startDate ? 'border-error-red' : 'border-panel-border'
                } focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm`}
              />
              {errors.startDate && (
                <p className="text-error-red text-xs font-mono mt-1">{errors.startDate}</p>
              )}
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                End Date *
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full bg-background border ${
                  errors.endDate ? 'border-error-red' : 'border-panel-border'
                } focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm`}
              />
              {errors.endDate && (
                <p className="text-error-red text-xs font-mono mt-1">{errors.endDate}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Optional notes about this contract..."
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm resize-none"
              />
            </div>
          </div>
        </GeometricPanel>

        {/* Product Prices */}
        <GeometricPanel>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display text-steel uppercase tracking-wider">
              Contract Prices
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Pricing Method:
                </label>
                <select
                  value={pricingMethod}
                  onChange={(e) => setPricingMethod(e.target.value)}
                  className="bg-background border border-panel-border focus:border-amber px-3 py-1 text-text-primary font-mono text-sm"
                >
                  {PRICING_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Add Products */}
          <div className="flex items-center space-x-4 mb-4">
            <select
              onChange={(e) => {
                handleAddProduct(e.target.value);
                e.target.value = '';
              }}
              className="flex-1 bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              defaultValue=""
            >
              <option value="" disabled>
                Add product...
              </option>
              {availableProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} ({product.sku})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddAllProducts}
              className="border border-steel text-steel hover:bg-steel/10 font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider whitespace-nowrap"
            >
              Add All
            </button>
          </div>

          {formData.prices.length === 0 ? (
            <div className="text-center py-8 text-text-tertiary font-mono">
              No products added yet. Add products above to set contract prices.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-panel-border">
                    <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Product
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Default
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      {getPriceLabel()}
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Min
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Max
                    </th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.prices.map((entry) => (
                    <tr
                      key={entry.productId}
                      className="border-b border-panel-border/50"
                    >
                      <td className="py-3 px-4">
                        <div className="font-mono text-text-primary">
                          {entry.product?.name}
                        </div>
                        <div className="text-text-tertiary text-xs">
                          {entry.product?.sku}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-text-secondary">
                        {formatPrice(entry.product?.pricePerUnit || null)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <input
                          type="number"
                          step={pricingMethod === 'MARGIN_PERCENT' ? '0.01' : '0.0001'}
                          value={getPriceValue(entry) ?? ''}
                          onChange={(e) =>
                            handlePriceChange(entry.productId, getPriceField(), e.target.value)
                          }
                          className="w-28 bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          placeholder={pricingMethod === 'MARGIN_PERCENT' ? '0.00' : '0.0000'}
                        />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          value={entry.minPrice ?? ''}
                          onChange={(e) =>
                            handlePriceChange(entry.productId, 'minPrice', e.target.value)
                          }
                          className="w-24 bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          placeholder="None"
                        />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          value={entry.maxPrice ?? ''}
                          onChange={(e) =>
                            handlePriceChange(entry.productId, 'maxPrice', e.target.value)
                          }
                          className="w-24 bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          placeholder="None"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(entry.productId)}
                          className="text-text-tertiary hover:text-error-red transition-colors"
                          title="Remove"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GeometricPanel>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <Link
            to="/pricing/contracts"
            className="px-6 py-3 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors font-mono uppercase tracking-wider"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update Contract' : 'Create Contract'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractFormPage;
